<template>
  <v-card class="glass-card profile-main-card pa-6 pa-md-8">
    <!-- User Header Hero -->
    <div class="d-flex flex-wrap align-center justify-space-between ga-4 mb-8">
      <div class="d-flex align-center ga-4">
        <div class="user-avatar-badge">
          <v-icon size="42" color="primary">mdi-account</v-icon>
        </div>
        <div>
          <div class="d-flex align-center ga-2 mb-1">
            <h1 class="text-h4 font-weight-bold">{{ user.firstname || user.name }} {{ user.lastname }}</h1>
            <v-chip
              size="small"
              :color="user.status === 'admin' ? 'purple' : 'primary'"
              variant="flat"
              class="font-weight-bold text-uppercase"
            >
              {{ user.status || 'Member' }}
            </v-chip>
          </div>
          <p class="text-body-2 text-medium-emphasis mb-0">@{{ user.name }} • Member since {{ formatDate(user.created_at) }}</p>
        </div>
      </div>

      <!-- Action Buttons Top/Right -->
      <div v-if="showActions" class="d-flex flex-wrap align-center ga-2">
        <v-btn
          variant="tonal"
          class="btn-modern"
          prepend-icon="mdi-pencil-outline"
          @click="openEditDialog"
        >
          Edit Details
        </v-btn>
        <v-btn
          variant="tonal"
          class="btn-modern"
          prepend-icon="mdi-key-outline"
          @click="changePwdDialog"
        >
          Password
        </v-btn>
      </div>
    </div>

    <!-- Info Grid -->
    <v-row dense class="mb-6">
      <v-col cols="12" sm="6">
        <div class="profile-info-tile pa-4 rounded-xl">
          <div class="text-caption text-medium-emphasis d-flex align-center ga-1 mb-1">
            <v-icon size="16">mdi-email-outline</v-icon> Email Address
          </div>
          <div class="text-body-1 font-weight-medium">{{ user.email || 'N/A' }}</div>
        </div>
      </v-col>

      <v-col cols="12" sm="6">
        <div class="profile-info-tile pa-4 rounded-xl">
          <div class="text-caption text-medium-emphasis d-flex align-center ga-1 mb-1">
            <v-icon size="16">mdi-office-building-outline</v-icon> Organization / Company
          </div>
          <div class="text-body-1 font-weight-medium">{{ user.company || 'Personal' }}</div>
        </div>
      </v-col>

      <v-col cols="12" sm="6">
        <div class="profile-info-tile pa-4 rounded-xl">
          <div class="text-caption text-medium-emphasis d-flex align-center ga-1 mb-1">
            <v-icon size="16">mdi-earth</v-icon> Country & Code
          </div>
          <div class="text-body-1 font-weight-medium">{{ user.countryname || 'Not set' }} {{ user.countrycode ? `(${user.countrycode})` : '' }}</div>
        </div>
      </v-col>

      <v-col cols="12" sm="6">
        <div class="profile-info-tile pa-4 rounded-xl">
          <div class="text-caption text-medium-emphasis d-flex align-center ga-1 mb-1">
            <v-icon size="16">mdi-shield-check-outline</v-icon> Account Status
          </div>
          <div class="text-body-1 font-weight-medium">
            <v-icon size="16" color="success" class="mr-1">mdi-check-circle</v-icon> Active & Verified
          </div>
        </div>
      </v-col>
    </v-row>

    <!-- Danger Zone: Delete Account -->
    <div v-if="showActions" class="mt-8 pt-6 border-t-subtle d-flex flex-wrap justify-space-between align-center ga-4">
      <div>
        <div class="text-subtitle-2 font-weight-bold text-error">Danger Zone</div>
        <div class="text-caption text-medium-emphasis">Permanently remove your account and all associated collections data</div>
      </div>
      <v-btn
        color="error"
        variant="tonal"
        size="small"
        class="btn-modern"
        prepend-icon="mdi-trash-can-outline"
        @click="deleteAccount"
      >
        Delete Account
      </v-btn>
    </div>

    <!-- Edit Profile Dialog -->
    <v-dialog v-model="dialog" max-width="600px">
      <v-card class="glass-card pa-6 rounded-2xl">
        <div class="d-flex align-center justify-space-between mb-4">
          <div class="d-flex align-center ga-2">
            <v-icon color="primary" size="24">mdi-account-edit</v-icon>
            <h3 class="text-h6 font-weight-bold mb-0">Edit Profile</h3>
          </div>
          <v-btn icon variant="text" size="small" @click="dialog = false">
            <v-icon size="20">mdi-close</v-icon>
          </v-btn>
        </div>

        <v-form ref="form">
          <v-row dense>
            <v-col cols="12" sm="6">
              <v-text-field v-model="editedUser.firstname" label="First Name" density="comfortable" variant="outlined" />
            </v-col>
            <v-col cols="12" sm="6">
              <v-text-field v-model="editedUser.lastname" label="Last Name" density="comfortable" variant="outlined" />
            </v-col>
            <v-col cols="12">
              <v-text-field v-model="editedUser.email" label="Email Address" type="email" density="comfortable" variant="outlined" />
            </v-col>
            <v-col cols="12">
              <v-text-field v-model="editedUser.company" label="Company / Organization" density="comfortable" variant="outlined" />
            </v-col>
            <v-col cols="12">
              <v-select
                v-model="selectedCountry"
                :items="countries"
                item-title="name"
                item-value="code"
                label="Country"
                return-object
                density="comfortable"
                variant="outlined"
              />
            </v-col>
          </v-row>
        </v-form>

        <div class="d-flex justify-end ga-2 mt-4">
          <v-btn variant="tonal" class="btn-modern" @click="dialog = false">Cancel</v-btn>
          <v-btn color="primary" class="btn-modern text-white font-weight-bold" @click="saveChanges">Save Profile</v-btn>
        </div>
      </v-card>
    </v-dialog>

    <!-- Change Password Dialog -->
    <v-dialog v-model="pwddialog" max-width="500px">
      <v-card class="glass-card pa-6 rounded-2xl">
        <div class="d-flex align-center justify-space-between mb-4">
          <div class="d-flex align-center ga-2">
            <v-icon color="primary" size="24">mdi-lock-reset</v-icon>
            <h3 class="text-h6 font-weight-bold mb-0">Change Password</h3>
          </div>
          <v-btn icon variant="text" size="small" @click="pwddialog = false">
            <v-icon size="20">mdi-close</v-icon>
          </v-btn>
        </div>

        <v-form ref="pwdForm">
          <v-text-field
            v-model="old_password"
            label="Current Password"
            type="password"
            density="comfortable"
            variant="outlined"
            class="mb-2"
          />
          <v-text-field
            v-model="new_password"
            label="New Password"
            hint="Min 8 characters (uppercase, lowercase, number, special)"
            type="password"
            density="comfortable"
            variant="outlined"
            :rules="passwordRules"
            class="mb-2"
          />
          <v-text-field
            v-model="confirm_password"
            label="Confirm New Password"
            type="password"
            density="comfortable"
            variant="outlined"
            class="mb-4"
          />
        </v-form>

        <div class="d-flex justify-end ga-2">
          <v-btn variant="tonal" class="btn-modern" @click="pwddialog = false">Cancel</v-btn>
          <v-btn color="primary" class="btn-modern text-white font-weight-bold" @click="savePassword">Update Password</v-btn>
        </div>
      </v-card>
    </v-dialog>

    <!-- Feedback Snackbars -->
    <v-snackbar v-model="showErrorSnackbar" :timeout="5000" color="error" location="top" rounded="lg">
      <div class="d-flex align-center">
        <v-icon class="mr-2">mdi-alert-circle</v-icon>
        {{ errorMessage }}
      </div>
    </v-snackbar>

    <v-snackbar v-model="showSuccessSnackbar" :timeout="5000" color="success" location="top" rounded="lg">
      <div class="d-flex align-center">
        <v-icon class="mr-2">mdi-check-circle</v-icon>
        {{ successMessage }}
      </div>
    </v-snackbar>
  </v-card>
</template>

<script>
import { computed, onMounted, ref, watch } from 'vue'
import { useRouter } from 'vue-router'
import { useStore } from 'vuex'
import api2 from '@/services/api2'
import Papa from 'papaparse'

export default {
  name: 'UserProfile',
  props: {
    user: {
      type: Object,
      required: true,
    },
    showActions: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['edit', 'update', 'changePwd', 'deleteAccount'],
  setup(props) {
    const dialog = ref(false)
    const editedUser = ref({})
    const store = useStore()
    const selectedCountry = ref(null)
    const countries = ref([])
    const countrycode = ref(null)

    const pwddialog = ref(false)
    const new_password = ref('')
    const confirm_password = ref('')
    const old_password = ref('')
    const pwdForm = ref(null)
    const showErrorSnackbar = ref(false)
    const errorMessage = ref('')
    const showSuccessSnackbar = ref(false)
    const successMessage = ref('')
    const router = useRouter()

    const passwordRules = computed(() => [
      v => !!v || 'Password is required',
      v => v.length >= 8 || 'Password must be at least 8 characters',
      v => /[A-Z]/.test(v) || 'Password must contain at least 1 uppercase letter',
      v => /[a-z]/.test(v) || 'Password must contain at least 1 lowercase letter',
      v => /[0-9]/.test(v) || 'Password must contain at least 1 digit',
      v => /[!@#$%^&*(),.?":{}|<>]/.test(v) || 'Password must contain at least 1 special character',
    ])

    watch(selectedCountry, (newValue) => {
      if (newValue && typeof newValue === 'object' && newValue.code) {
        editedUser.value.countryname = newValue.name
        editedUser.value.countrycode = newValue.code
      } else if (newValue) {
        countrycode.value = newValue
      }
    })

    const loadCSV = async () => {
      try {
        const response = await fetch('countriesdb.csv')
        const csvData = await response.text()
        Papa.parse(csvData, {
          header: true,
          skipEmptyLines: true,
          complete: (result) => {
            countries.value = result.data.map(item => ({
              code: item.code,
              name: item.name,
            }))
          },
          error: (error) => {
            console.error('Error parsing CSV:', error)
          },
        })
      } catch (error) {
        console.error('Error fetching CSV:', error)
      }
    }

    onMounted(() => {
      loadCSV()
    })

    const formatDate = (dateString) => {
      if (!dateString) return 'N/A'
      return new Date(dateString).toLocaleDateString(undefined, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      })
    }

    const openEditDialog = async () => {
      editedUser.value = { ...props.user }
      selectedCountry.value = editedUser.value.countryname
      countrycode.value = editedUser.value.countrycode
      dialog.value = true
    }

    const changePwdDialog = async () => {
      editedUser.value = { ...props.user }
      pwddialog.value = true
      old_password.value = ''
      new_password.value = ''
      confirm_password.value = ''
    }

    const deleteAccount = async () => {
      if (!confirm('Are you sure you want to delete your account? This action cannot be undone.')) {
        return
      }

      try {
        await api2.delete('/api/users/me/')
        localStorage.removeItem('token')
        localStorage.removeItem('user')
        store.dispatch('auth/logout')
        router.push('/')
        successMessage.value = 'Account successfully deleted'
        showSuccessSnackbar.value = true
      } catch (error) {
        console.error('Error deleting account:', error)
        errorMessage.value = 'Error: Account deletion failed. Contact the administrator.'
        showErrorSnackbar.value = true
      }
    }

    const saveChanges = async () => {
      try {
        await api2.post('/api/users/me/', editedUser.value)
        await store.dispatch('auth/fetchUser')
        dialog.value = false
        successMessage.value = 'Profile updated successfully!'
        showSuccessSnackbar.value = true
      } catch (error) {
        console.error('Error updating profile:', error)
        if (error.response?.status === 400 && error.response?.data?.detail === 'Email already exists.') {
          errorMessage.value = 'Error: Email already exists'
        } else {
          errorMessage.value = 'Error: Profile update failed. Contact the administrator.'
        }
        showErrorSnackbar.value = true
      }
    }

    const savePassword = async () => {
      try {
        const { valid } = await pwdForm.value.validate()
        if (!valid) {
          if (new_password.value) {
            const errors = []
            if (new_password.value.length < 8) errors.push('at least 8 characters')
            if (!/[A-Z]/.test(new_password.value)) errors.push('1 uppercase letter')
            if (!/[a-z]/.test(new_password.value)) errors.push('1 lowercase letter')
            if (!/[0-9]/.test(new_password.value)) errors.push('1 digit')
            if (!/[!@#$%^&*(),.?":{}|<>]/.test(new_password.value)) errors.push('1 special character')

            if (errors.length > 0) {
              errorMessage.value = `Error: Password must contain ${errors.join(', ')}`
              showErrorSnackbar.value = true
            }
          }
          return
        }

        if (new_password.value !== confirm_password.value) {
          errorMessage.value = 'Error: New password and confirm password do not match!'
          showErrorSnackbar.value = true
          return
        }

        const payload = {
          old_password: old_password.value,
          new_password: new_password.value,
          confirm_password: confirm_password.value,
        }

        const response = await api2.put('/api/users/change-password/', payload)

        if (response.status === 200) {
          successMessage.value = 'Password has been successfully changed!'
          showSuccessSnackbar.value = true
        }

        old_password.value = ''
        new_password.value = ''
        confirm_password.value = ''
        pwddialog.value = false
      } catch (error) {
        console.error('Error changing password:', error)
        errorMessage.value = 'Error: Password update failed.'
        showErrorSnackbar.value = true
      }
    }

    return {
      formatDate,
      dialog,
      editedUser,
      openEditDialog,
      saveChanges,
      countrycode,
      selectedCountry,
      countries,
      changePwdDialog,
      pwddialog,
      new_password,
      confirm_password,
      old_password,
      pwdForm,
      savePassword,
      showErrorSnackbar,
      errorMessage,
      showSuccessSnackbar,
      successMessage,
      deleteAccount,
      passwordRules,
    }
  },
}
</script>

<style scoped>
.profile-main-card {
  border-radius: 24px !important;
}

.user-avatar-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 72px;
  height: 72px;
  border-radius: 24px;
  background: rgba(99, 102, 241, 0.12);
  border: 1px solid rgba(99, 102, 241, 0.25);
  box-shadow: 0 8px 24px rgba(99, 102, 241, 0.15);
}

.profile-info-tile {
  background: rgba(128, 128, 128, 0.05);
  border: 1px solid rgba(128, 128, 128, 0.12);
  transition: transform 0.2s ease, border-color 0.2s ease;
}

.profile-info-tile:hover {
  transform: translateY(-2px);
  border-color: rgba(99, 102, 241, 0.3);
}

.border-t-subtle {
  border-top: 1px solid rgba(128, 128, 128, 0.12);
}
</style>